import { Box, Button, Heading, Strong, Text } from '@radix-ui/themes';
import { CsvExportHelper } from 'classes/helpers/csv-export.helper';
import { NotifyHelper } from 'classes/helpers/notify.helper';
import { SuperAdminIcon } from 'components/common/custom-icon/shorthands';
import { CommonDialog } from 'components/common/dialogs';
import { ErrorBoundary } from 'components/common/error-boundary';
import { CommonFormButton } from 'components/common/form/button';
import { CommonFormGrid } from 'components/common/form/grid';
import { CommonTextInput } from 'components/common/form/text';
import { CommonTextareaInput } from 'components/common/form/textarea';
import { MachineContext } from 'contexts/machine.context';
import { SessionEventsContext } from 'contexts/session-events.context';
import { t } from 'i18next';
import { IBaseDialog } from 'interfaces/i-dialogs';
import { RADIX } from 'lib_ts/enums/radix-ui';
import {
  ILoginEvent,
  ILoginEventData,
} from 'lib_ts/interfaces/i-session-event';
import { ISessionSummary } from 'lib_ts/interfaces/i-session-summary';
import { SpecialMsPosition } from 'lib_ts/interfaces/machine-msg/i-special-mstarget';
import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { SessionEventsService } from 'services/session-events.service';
import { useAuthStore } from 'stores/use-auth-store';

interface IProps extends IBaseDialog {
  session: Partial<ISessionSummary>;
  fires: number;
  onChanged: () => void;
}

export const EditSessionDialog = ({
  session,
  fires,
  onChanged,
  onClose,
  identifier,
}: IProps) => {
  const current = useAuthStore((s) => s.current);
  const userRoleFeatureFlags = useAuthStore((s) => s.userRoleFeatureFlags);

  const { getPitchStatsData } = useContext(SessionEventsContext);
  const { specialMsTarget } = useContext(MachineContext);

  const [loading, setLoading] = useState(false);
  const [loginData, setLoginData] = useState<Partial<ILoginEvent>>({});

  const initRef = useRef(false);

  useEffect(() => {
    if (initRef.current || !session.sessionID) {
      return;
    }

    initRef.current = true;

    const getLogin = async () => {
      setLoading(true);

      if (!session.sessionID) {
        return;
      }

      try {
        const login = await SessionEventsService.getLogin(session.sessionID);

        if (!login) {
          NotifyHelper.warning({
            message_md: `Login event could not be found for session \`${session.sessionID}\`.`,
          });
          return;
        }

        setLoginData({
          _id: login._id,
          data: login.data ?? {},
        });
      } finally {
        setLoading(false);
      }
    };

    getLogin();
  }, [session.sessionID]);

  const setData = (model: Partial<ILoginEventData>) => {
    setLoginData((prev) => ({
      ...prev,
      data: {
        ...prev.data,
        ...model,
      },
    }));
  };

  const renderContent = useCallback(() => {
    const isSuper = current.mode === 'super';
    const showSuperMode =
      userRoleFeatureFlags.controls.superMode &&
      current.session === session.sessionID;

    return (
      <CommonFormGrid columns={3}>
        {showSuperMode && (
          <>
            <Box gridColumn="span 3">
              <Heading
                size={RADIX.HEADING.SIZE.MD}
                color={RADIX.COLOR.SUPER_ADMIN}
              >
                Super Access
              </Heading>
            </Box>
            <Box gridColumn="span 2">
              <p>
                Super mode allows access to video data from all teams and
                super-only pitch lists.
              </p>
              <p>
                <Text color={RADIX.COLOR.DANGER}>
                  <Strong>
                    Any changes to video metadata will impact other users!
                  </Strong>
                </Text>
              </p>
            </Box>
            <Box className="valign-top">
              <Button
                id="session-toggle-super"
                className="btn-block"
                variant={
                  isSuper
                    ? RADIX.BUTTON.VARIANT.SELECTED
                    : RADIX.BUTTON.VARIANT.NOT_SELECTED
                }
                color={isSuper ? RADIX.COLOR.SUCCESS : RADIX.COLOR.SUPER_ADMIN}
                onClick={async () => {
                  const success = await useAuthStore
                    .getState()
                    .toggleSuperSession();

                  if (success) {
                    onClose();
                  }
                }}
              >
                {`Toggle: ${isSuper ? 'OFF' : 'ON'}`}
              </Button>
            </Box>
          </>
        )}

        <Box gridColumn="span 2">
          <CommonFormButton
            id="edit-session-identifier"
            title={t('common.copy-to-clipboard').toString()}
            className="font-mono"
            variant="soft"
            inputColor={RADIX.COLOR.WARNING}
            label={session.sessionID}
            onClick={async () => {
              if (!session.sessionID) {
                return;
              }

              await navigator.clipboard.writeText(session.sessionID);

              NotifyHelper.success({
                message_md: t('common.x-copied-to-clipboard', {
                  x: t('common.identifier'),
                }),
              });
            }}
          />
        </Box>
        <Box>
          <CommonTextInput
            id="edit-session-shots"
            label="common.shots"
            value={fires.toString()}
            disabled
          />
        </Box>

        {loginData.data && (
          <>
            <Box gridColumn="span 3">
              <CommonTextInput
                id="edit-session-name"
                label="common.name"
                value={loginData.data.name}
                disabled={loading}
                onChange={(v) => setData({ name: v ?? '' })}
                optional
              />
            </Box>
            <Box gridColumn="span 3">
              <CommonTextareaInput
                id="edit-session-notes"
                label="common.notes"
                value={loginData.data.notes}
                disabled={loading}
                onChange={(v) => setData({ notes: v ?? '' })}
                optional
              />
            </Box>
          </>
        )}
      </CommonFormGrid>
    );
  }, [
    current.mode,
    session.sessionID,
    fires,
    loginData,
    loading,
    setData,
    onClose,
    userRoleFeatureFlags.controls.superMode,
  ]);

  return (
    <ErrorBoundary identifier="EditSessionDialog">
      <CommonDialog
        identifier={identifier}
        title={t('common.edit-x', {
          x: t('common.session'),
        }).toString()}
        loading={loading}
        width={RADIX.DIALOG.WIDTH.MD}
        content={renderContent()}
        buttons={[
          {
            label: 'common.export-stats',
            color: RADIX.COLOR.INFO,
            invisible: !userRoleFeatureFlags.analytics.exportStats,
            icon: <SuperAdminIcon />,
            onClick: async () => {
              if (!session.sessionID) {
                return;
              }

              const data = await getPitchStatsData({
                session: session.sessionID,
              });

              if (data.length === 0) {
                NotifyHelper.warning({
                  message_md: t('common.session-has-no-pitch-stats'),
                });
                return;
              }

              CsvExportHelper.saveCsv(data, {
                prefix: `session-${session.sessionID}_stats`,
              });
            },
          },
          {
            label: t('common.new-x', {
              x: t('common.session'),
            }).toString(),
            color: RADIX.COLOR.SUCCESS,
            invisible: current.session !== session.sessionID,
            onClick: () =>
              useAuthStore
                .getState()
                .newSession()
                .then((success) => {
                  if (success) {
                    onClose();
                    specialMsTarget(SpecialMsPosition.lowered);
                  }
                }),
          },
          {
            label: 'common.save',
            color: RADIX.COLOR.ACCENT,
            variant: 'solid',
            disabled: !loginData._id || !loginData.data,
            onClick: () => {
              setLoading(true);
              SessionEventsService.updateOne({
                _id: loginData._id,
                data: loginData.data ?? {},
              })
                .then(() =>
                  NotifyHelper.success({
                    message_md: t('common.x-updated-successfully', {
                      x: t('common.session'),
                    }),
                  })
                )
                .finally(() => {
                  onChanged();
                  onClose();
                });
            },
          },
        ]}
        onClose={onClose}
      />
    </ErrorBoundary>
  );
};
